import { useState } from "react";

import Button from "../../components/Shared/Button";
import { ButtonTypes } from "../../utils/consts";
import Input from "../../components/Shared/Input";
import TextArea from "../../components/Shared/TextArea";
import MultipleUpload from "../../components/Shared/MultipleUpload";
import Upload from "../../components/Shared/Upload";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Selectbox from "../../components/Shared/SelectBox";

const CreateQuizType = z.object({
  title: z.string().min(1, { message: "Quiz title is required" }),
});
type CreateQuizFormData = z.infer<typeof CreateQuizType>;

const CreateQuestionType = z.object({
  name: z.string().min(1, { message: "Question name is required" }),
  choices: z
    .array(z.string().min(1, { message: "Answer is required" }))
    .length(4, { message: "4 choices are required" }),
  correctAnswerIndex: z
    .number()
    .min(0, { message: "Correct answer index is required" }),
  explanation: z.string().optional(),
  attachment: z.string().optional(),
  figure: z.array(z.string()).optional(),
});
type CreateQuestionFormData = z.infer<typeof CreateQuestionType>;

const CreateHostQuiz = () => {
  const [questionsList, setQuestionsList] = useState<
    ({
      id: number;
    } & CreateQuestionFormData)[]
  >([]);
  console.log(questionsList);

  // Quiz Creation Form
  const [quiz, setQuiz] = useState<CreateQuizFormData | null>(null);
  const {
    control: quizCreationControl,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<CreateQuizFormData>({
    resolver: zodResolver(CreateQuizType),
  });
  const onQuizCreation: SubmitHandler<CreateQuizFormData> = (data) => {
    setQuiz(data);
  };

  // Question Creation Form
  const [selectedQuestion, setSelectedQuestion] = useState<any>(null);
  const {
    control: questionCreationControl,
    handleSubmit: handleQuestionSubmit,
    setValue: setQuestionValue,
    watch: watchQuestionValues,
    formState: { isSubmitting: isQuestionSubmitting },
  } = useForm<CreateQuestionFormData>({
    resolver: zodResolver(CreateQuestionType),
    defaultValues: {
      choices: ["", "", "", ""],
      correctAnswerIndex: 0,
    },
  });
  const questionValues = watchQuestionValues();
  const onQuestionCreation: SubmitHandler<CreateQuestionFormData> = (data) => {
    setQuestionsList([
      ...questionsList,
      { ...data, id: questionsList.length + 1 },
    ]);

    resetFormValues();
  };
  const onQuestionUpdate: SubmitHandler<CreateQuestionFormData> = (data) => {
    const updateQuestionId = selectedQuestion.id;
    setSelectedQuestion({
      name: "Add New Question",
      id: "-1",
      count: 5,
    });
    setQuestionsList((prevQuestions: any) =>
      prevQuestions.map((question: any) =>
        question.id == updateQuestionId ? data : question
      )
    );

    resetFormValues();
  };

  const resetFormValues = (values?: CreateQuestionFormData) => {
    if (values) {
      console.log("Reset values", values);
      setQuestionValue("name", values.name);
      setQuestionValue("choices", values.choices);
      setQuestionValue("correctAnswerIndex", values.correctAnswerIndex);
      setQuestionValue("explanation", values.explanation);
      setQuestionValue("attachment", values.attachment);
      setQuestionValue("figure", values.figure);
    } else {
      setQuestionValue("name", "");
      setQuestionValue("choices", ["", "", "", ""]);
      setQuestionValue("correctAnswerIndex", 0);
      setQuestionValue("explanation", "");
      setQuestionValue("attachment", "");
    }
  };

  return (
    <div className="py-7">
      <h1 className="text-burntBlack font-bold text-[32px] mb-6">
        Host new quiz
      </h1>
      <form
        onSubmit={handleSubmit(onQuizCreation)}
        className="p-8 rounded-3xl shadow bg-white border border-black/15 space-y-5"
      >
        <div className="w-full mx-auto">
          <Controller
            control={quizCreationControl}
            name="title"
            render={({ field, fieldState }) => (
              <Input
                {...field}
                error={fieldState.error?.message}
                className="text-lg font-semibold"
                inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                title="Quiz Title"
                placeholder="Enter Quiz Title..."
              />
            )}
          />
        </div>

        <div className="flex justify-end w-full">
          <Button
            type={ButtonTypes.SUBMIT}
            className={`mt-8 w-[160px] !h-11 !text-base font-bold`}
            variant="primary"
            disabled={isSubmitting}
          >
            {!quiz ? "Create Quiz" : "Update Quiz"}
          </Button>
        </div>
      </form>

      {quiz && (
        <form
          onSubmit={handleQuestionSubmit(
            selectedQuestion ? onQuestionUpdate : onQuestionCreation
          )}
          className="p-8 rounded-3xl shadow bg-white border border-black/15 space-y-5 mt-10"
        >
          {questionsList.length > 0 && (
            <div>
              <Selectbox
                selected={selectedQuestion}
                setSelected={(value) => {
                  resetFormValues(
                    questionsList.find(
                      (q, idx) => `${idx + 1} - ${q.name}` == value?.name
                    )
                  );
                  setSelectedQuestion(value);
                }}
                selectOptions={[
                  ...questionsList.map((q, idx) => ({
                    name: `${idx + 1} - ${q.name}`,
                    id: q.id.toString(),
                  })),
                  {
                    name: "Add New Question",
                    id: "-1",
                    count: 5,
                  },
                ]}
                title="Quiz Type"
                placeholder="Select Question or add new"
                name="quizType"
              ></Selectbox>
            </div>
          )}

          <div>
            <h1 className="text-2xl font-bold mb-2.5">Questions</h1>
            <p className="text-lightGray font-medium">
              Nice text goes here to explain the question creation part like
              never before
            </p>
          </div>

          <div className="mt-9">
            <Controller
              control={questionCreationControl}
              name="name"
              render={({ field, fieldState }) => (
                <Input
                  className="text-lg font-semibold"
                  inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                  title="Question name"
                  placeholder=""
                  name="name"
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  type="text"
                />
              )}
            />
            <p className="text-lightGray font-medium mb-4 mt-4">
              Please mark the correct answer among them
            </p>
            <div className="grid-cols-2 grid gap-x-6 gap-y-4">
              {Array.from({ length: 4 }).map((_, choiceIndex) => (
                <div key={`choice-${choiceIndex}`} className="mb-4">
                  <label
                    htmlFor={`choice-radio-${choiceIndex}`}
                    className="text-lg font-semibold flex items-center justify-between mb-2"
                    onClick={() => {
                      setQuestionValue("correctAnswerIndex", choiceIndex);
                    }}
                  >
                    <span>{`Choice ${choiceIndex + 1}`}</span>

                    <input
                      id={`choice-radio-${choiceIndex}`}
                      type="radio"
                      name={`choice`}
                      value={choiceIndex}
                      checked={
                        questionValues.correctAnswerIndex === choiceIndex
                      }
                      className="w-5 h-5 text-primary bg-gray-100 border-black/30 border-2 focus:ring-0"
                    />
                  </label>

                  <Controller
                    control={questionCreationControl}
                    name={`choices.${choiceIndex}`}
                    render={({ field, fieldState }) => (
                      <Input
                        className="text-lg font-semibold"
                        inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                        placeholder="Enter Choice..."
                        name={`choice-${choiceIndex}`}
                        value={field.value}
                        error={fieldState.error?.message}
                        onChange={field.onChange}
                        type="text"
                      />
                    )}
                  />
                </div>
              ))}
            </div>

            <Controller
              control={questionCreationControl}
              name="explanation"
              render={({ field, fieldState }) => (
                <TextArea
                  className="text-lg font-semibold mb-10"
                  inputStyles="!bg-[#F5F6F8] h-24 border-black/15 !rounded"
                  title="Explanation (Optional)"
                  placeholder=""
                  name="explanation"
                  value={field.value}
                  error={fieldState.error?.message}
                  onChange={field.onChange}
                />
              )}
            />

            <Controller
              control={questionCreationControl}
              name="attachment"
              render={({ field }) => (
                <MultipleUpload
                  folder="courses_images"
                  onFileUploaded={(url) => {
                    field.onChange(url);
                  }}
                  questionId={questionsList.length + 1}
                >
                  <Input
                    upload={!isQuestionSubmitting}
                    className="text-lg font-semibold mb-10"
                    inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                    placeholder=""
                    title="Figures"
                    name="img_url"
                    type="text"
                  />
                </MultipleUpload>
              )}
            />

            <Controller
              control={questionCreationControl}
              name="attachment"
              render={({ field }) => (
                <Upload
                  acceptedFiles={{ pdf: [] }}
                  folder="quiz_resources"
                  onFileUploaded={(url) => {
                    field.onChange(url);
                  }}
                >
                  <Input
                    upload={!isQuestionSubmitting}
                    className="text-lg font-semibold"
                    inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                    placeholder=""
                    title="Attachments"
                    name="attachment"
                    type="text"
                  />
                </Upload>
              )}
            />
          </div>
          <div className="flex justify-end gap-5 mt-10">
            <Button
              type={ButtonTypes.SUBMIT}
              variant="primary"
              className="!h-12 w-40 !text-base !font-bold "
            >
              {selectedQuestion ? "Update Question" : "Save & Add New"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default CreateHostQuiz;
